import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  UserIcon,
  BuildingOfficeIcon,
  LockClosedIcon,
  InformationCircleIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

import { updateProfile, updatePassword } from '../../api/authApi';
import { useAuth }                        from '../../context/AuthContext';
import Card    from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';

// ── Schemas ───────────────────────────────────────────────────────────────────
const profileSchema = yup.object({
  name:  yup.string().trim().required('Name is required').min(2),
  email: yup.string().email('Invalid email').required(),
});

const passwordSchema = yup.object({
  current_password:      yup.string().required('Current password is required'),
  new_password:          yup.string().min(8, 'At least 8 characters').required('New password is required'),
  new_password_confirm:  yup.string()
    .oneOf([yup.ref('new_password')], 'Passwords do not match')
    .required('Please confirm your new password'),
});

// ── Tabs config ───────────────────────────────────────────────────────────────
const TABS = [
  { key: 'profile',      label: 'Profile',      Icon: UserIcon },
  { key: 'organization', label: 'Organization',  Icon: BuildingOfficeIcon },
  { key: 'password',     label: 'Password',      Icon: LockClosedIcon },
  { key: 'about',        label: 'About',         Icon: InformationCircleIcon },
];

// ── Password Field with toggle ────────────────────────────────────────────────
function PasswordField({ label, error, registration, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-neutral-700">{label}</label>
      <div className="relative">
        <input
          {...registration}
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          className={clsx(
            'w-full rounded-input border py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100',
            error
              ? 'border-error-400 focus:border-error-400'
              : 'border-neutral-200 focus:border-primary-400'
          )}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition"
          tabIndex={-1}
        >
          {show ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-error-500">{error.message}</p>}
    </div>
  );
}

// ── Copy to clipboard button ──────────────────────────────────────────────────
function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="ml-2 inline-flex items-center gap-1 rounded-button px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition"
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5 text-success-500" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ── Profile Tab ───────────────────────────────────────────────────────────────
function ProfileTab({ user, onUserUpdate }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name:  user?.name  ?? '',
      email: user?.email ?? '',
    },
  });

  const onSubmit = async (values) => {
    try {
      const res = await updateProfile({ name: values.name });
      onUserUpdate?.(res.data.data ?? res.data);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Failed to update profile');
    }
  };

  return (
    <Card title="Profile Information">
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-md space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Full Name <span className="text-error-500">*</span>
          </label>
          <input
            {...register('name')}
            placeholder="Your name"
            className={clsx(
              'w-full rounded-input border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100',
              errors.name
                ? 'border-error-400 focus:border-error-400'
                : 'border-neutral-200 focus:border-primary-400'
            )}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-error-500">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Email
          </label>
          <input
            {...register('email')}
            readOnly
            className="w-full cursor-not-allowed rounded-input border border-neutral-100 bg-neutral-50 px-3 py-2 text-sm text-neutral-400"
          />
          <p className="mt-1 text-xs text-neutral-400">
            Email cannot be changed. Contact support if needed.
          </p>
        </div>

        <div className="pt-1">
          <button
            type="submit"
            disabled={isSubmitting || !isDirty}
            className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {isSubmitting && <Spinner size="sm" className="text-white" />}
            Save Changes
          </button>
        </div>
      </form>
    </Card>
  );
}

// ── Organization Tab ──────────────────────────────────────────────────────────
function OrganizationTab({ user }) {
  const org = user?.organization ?? user?.org ?? null;

  const Field = ({ label, value, copyable }) => (
    <div>
      <label className="mb-1 block text-sm font-medium text-neutral-700">{label}</label>
      <div className="flex items-center">
        <div className="flex-1 rounded-input border border-neutral-100 bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
          {value ?? '—'}
        </div>
        {copyable && value && <CopyButton value={value} />}
      </div>
    </div>
  );

  return (
    <Card title="Organization Details">
      <div className="max-w-md space-y-5">
        <Field label="Organization Name" value={org?.name ?? user?.org_name} />
        <Field
          label="Organization Code"
          value={org?.code ?? user?.org_code}
          copyable
        />
        <Field label="Your Role"  value={user?.role} />
        <Field label="Member Since" value={
          user?.created_at
            ? new Date(user.created_at).toLocaleDateString(undefined, {
                day: 'numeric', month: 'long', year: 'numeric',
              })
            : null
        } />

        <div className="rounded-card border border-neutral-100 bg-neutral-50 p-4">
          <p className="text-xs text-neutral-500">
            Organization settings can only be changed by an admin. Contact your
            organization administrator to update these details.
          </p>
        </div>
      </div>
    </Card>
  );
}

// ── Password Tab ──────────────────────────────────────────────────────────────
function PasswordTab() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(passwordSchema) });

  const onSubmit = async (values) => {
    try {
      await updatePassword({
        current_password: values.current_password,
        new_password:     values.new_password,
      });
      toast.success('Password updated successfully');
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Failed to update password');
    }
  };

  return (
    <Card title="Change Password">
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-md space-y-5">
        <PasswordField
          label="Current Password"
          registration={register('current_password')}
          error={errors.current_password}
          placeholder="Enter current password"
        />
        <PasswordField
          label="New Password"
          registration={register('new_password')}
          error={errors.new_password}
          placeholder="At least 8 characters"
        />
        <PasswordField
          label="Confirm New Password"
          registration={register('new_password_confirm')}
          error={errors.new_password_confirm}
          placeholder="Re-enter new password"
        />

        {/* Password requirements hint */}
        <div className="rounded-input bg-neutral-50 px-3 py-2">
          <ul className="space-y-1 text-xs text-neutral-500">
            <li>• Minimum 8 characters</li>
            <li>• Use a mix of letters, numbers, and symbols for better security</li>
          </ul>
        </div>

        <div className="pt-1">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {isSubmitting && <Spinner size="sm" className="text-white" />}
            Update Password
          </button>
        </div>
      </form>
    </Card>
  );
}

// ── About Tab ─────────────────────────────────────────────────────────────────
function AboutTab() {
  const APP_VERSION = '1.0.0';

  return (
    <Card title="About FounderMind AI">
      <div className="max-w-md space-y-6">
        {/* App info */}
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-card bg-primary-600 text-white shadow-card">
            <span className="text-xl font-heading font-bold">FM</span>
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-neutral-900">
              FounderMind AI
            </p>
            <p className="text-sm text-neutral-500">Version {APP_VERSION}</p>
          </div>
        </div>

        <p className="text-sm text-neutral-600 leading-relaxed">
          FounderMind AI helps accelerators and investors evaluate startup founders
          through AI-powered assessments, providing deep insights into team potential,
          market understanding, and execution capability.
        </p>

        {/* Links */}
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-input border border-neutral-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-neutral-700">Documentation</p>
              <p className="text-xs text-neutral-400">Platform guides and API reference</p>
            </div>
            <a
              href="https://docs.foundermind.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition"
            >
              Open →
            </a>
          </div>

          <div className="flex items-center justify-between rounded-input border border-neutral-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-neutral-700">Support</p>
              <p className="text-xs text-neutral-400">Get help from our team</p>
            </div>
            <a
              href="mailto:support@foundermind.ai"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition"
            >
              support@foundermind.ai
            </a>
          </div>

          <div className="flex items-center justify-between rounded-input border border-neutral-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-neutral-700">Privacy Policy</p>
              <p className="text-xs text-neutral-400">How we handle your data</p>
            </div>
            <a
              href="https://foundermind.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition"
            >
              Read →
            </a>
          </div>
        </div>

        <p className="text-xs text-neutral-400">
          © {new Date().getFullYear()} FounderMind AI. All rights reserved.
        </p>
      </div>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const { user, login } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const handleUserUpdate = (updatedUser) => {
    // Preserve token, update user data in context
    const token = localStorage.getItem('fm_token');
    if (token) login(token, updatedUser);
  };

  return (
    <div className="min-h-screen bg-surface p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-neutral-900">Settings</h1>
        <p className="mt-0.5 text-sm text-neutral-500">
          Manage your account and preferences
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Sidebar Nav */}
        <nav className="flex shrink-0 flex-col gap-1 lg:w-52">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={clsx(
                'flex items-center gap-3 rounded-card px-4 py-2.5 text-sm font-medium transition text-left',
                activeTab === key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && (
            <ProfileTab user={user} onUserUpdate={handleUserUpdate} />
          )}
          {activeTab === 'organization' && <OrganizationTab user={user} />}
          {activeTab === 'password'     && <PasswordTab />}
          {activeTab === 'about'        && <AboutTab />}
        </div>
      </div>
    </div>
  );
}
